import React, { useState, useEffect } from 'react';
import api from '../api';
import { format } from 'date-fns';

const ManageRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [year, setYear] = useState('upcoming');
  const [declineModal, setDeclineModal] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const typeLabels = { holiday: 'Holiday', sick: 'Sick Day', awol: 'Absent Without Leave' };

  const load = async () => {
    setLoading(true);
    try {
      // Pending & cancellations must show across ALL years so a future-dated
      // request is never hidden from approval. Other tabs respect the year picker.
      const yr = (filter === 'pending' || filter === 'cancellations') ? 'all' : year;
      const params = new URLSearchParams({ year: yr });
      if (filter === 'cancellations') params.append('status', 'approved');
      else if (filter !== 'all') params.append('status', filter);
      const res = await api.get(`/api/holidays/all?${params}`);
      setRequests(res.data);
    } catch (err) {
      setError('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter, year]);

  const handleApprove = async (id) => {
    try {
      await api.patch(`/api/holidays/${id}/status`, { status: 'approved' });
      setSuccess('Request approved');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve');
    }
  };

  const handleCancellation = async (id, decision) => {
    try {
      await api.patch(`/api/holidays/${id}/cancellation`, { decision });
      setSuccess(decision === 'approve' ? 'Holiday cancelled — days returned to their allowance' : 'Cancellation declined — holiday stands');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to action cancellation');
    }
  };

  const handleDirectCancel = async (id) => {
    if (!window.confirm('Cancel this holiday? The days will go back onto the employee\'s allowance and they will be emailed.')) return;
    try {
      await api.post(`/api/holidays/${id}/cancel`);
      setSuccess('Holiday cancelled — days returned to their allowance');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel holiday');
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) return setError('Please provide a reason for declining');
    try {
      await api.patch(`/api/holidays/${declineModal}/status`, { status: 'declined', decline_reason: declineReason });
      setSuccess('Request declined');
      setDeclineModal(null);
      setDeclineReason('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to decline');
    }
  };

  const years = [new Date().getFullYear() + 1, new Date().getFullYear(), new Date().getFullYear() - 1];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Manage Requests</h1>
          <p className="page-subtitle">Review and action staff absence requests</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <select className="form-control" style={{ width: 'auto' }} value={year} onChange={e => { const v = e.target.value; setYear(v === 'all' || v === 'upcoming' ? v : Number(v)); }} disabled={filter === 'pending' || filter === 'cancellations'} title={filter === 'pending' || filter === 'cancellations' ? 'Showing all years' : ''}>
            <option value="upcoming">This year &amp; upcoming</option>
            <option value="all">All years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error" onClick={() => setError('')}>{error}</div>}
      {success && <div className="alert alert-success" onClick={() => setSuccess('')}>{success}</div>}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '6px', padding: '4px', width: 'fit-content' }}>
        {['pending', 'approved', 'declined', 'cancellations', 'all'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '8px 18px', border: 'none', borderRadius: '4px', cursor: 'pointer',
            fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: '500', letterSpacing: '0.5px',
            textTransform: 'capitalize',
            background: filter === f ? 'var(--navy)' : 'transparent',
            color: filter === f ? 'var(--white)' : 'var(--grey-mid)',
            transition: 'all 0.15s'
          }}>
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div className="loader">Loading...</div>
        ) : requests.length === 0 ? (
          <div className="empty-state">
            <h3>No {filter !== 'all' ? filter : ''} requests</h3>
            <p>Nothing to show for the selected filters</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Staff Member</th>
                  <th>Type</th>
                  <th>Dates</th>
                  <th>Days</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontWeight: '500' }}>{r.staff_name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--grey-mid)' }}>{r.job_title || r.department || ''}</div>
                    </td>
                    <td><span className={`badge badge-${r.absence_type}`}>{typeLabels[r.absence_type]}</span></td>
                    <td style={{ fontSize: '13px' }}>
                      {format(new Date(r.start_date.substring(0,10) + 'T00:00:00'), 'd MMM yy')}
                      {r.start_date !== r.end_date && <> &mdash; {format(new Date(r.end_date.substring(0,10) + 'T00:00:00'), 'd MMM yy')}</>}
                    </td>
                    <td>{r.days_taken}{r.half_day ? ` (½ ${r.half_day_period === 'pm' ? 'PM' : 'AM'})` : ''}</td>
                    <td style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>{format(new Date(r.created_at), 'd MMM')}</td>
                    <td>
                      <span className={`badge badge-${r.status}`}>{r.status.charAt(0).toUpperCase() + r.status.slice(1)}</span>
                      {r.status === 'declined' && r.decline_reason && (
                        <div style={{ fontSize: '11px', color: 'var(--danger)', marginTop: '3px' }}>{r.decline_reason}</div>
                      )}
                      {r.status === 'approved' && r.cancellation_requested && (
                        <div style={{ fontSize: '11px', color: 'var(--red)', marginTop: '3px', fontWeight: 600 }}>
                          Cancellation requested{r.cancellation_reason ? `: ${r.cancellation_reason}` : ''}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: '12px', color: 'var(--grey-mid)', maxWidth: '150px' }}>{r.notes || '-'}</td>
                    <td>
                      {r.status === 'pending' && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button className="btn btn-success btn-sm" onClick={() => handleApprove(r.id)}>Approve</button>
                          <button className="btn btn-danger btn-sm" onClick={() => { setDeclineModal(r.id); setDeclineReason(''); }}>Decline</button>
                        </div>
                      )}
                      {r.status === 'approved' && r.cancellation_requested && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button className="btn btn-success btn-sm" onClick={() => handleCancellation(r.id, 'approve')}>Approve cancellation</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleCancellation(r.id, 'decline')}>Decline</button>
                        </div>
                      )}
                      {filter === 'cancellations' && r.status === 'approved' && !r.cancellation_requested && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDirectCancel(r.id)}>Cancel holiday</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Decline modal */}
      {declineModal && (
        <div className="modal-overlay" onClick={() => setDeclineModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Decline Request</h3>
              <button className="modal-close" onClick={() => setDeclineModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--grey-mid)', fontSize: '13px', marginBottom: '16px' }}>
                Please provide a reason. This will be included in the email notification to the staff member.
              </p>
              <div className="form-group">
                <label className="form-label">Reason for declining</label>
                <textarea className="form-control" value={declineReason} onChange={e => setDeclineReason(e.target.value)} placeholder="e.g. Insufficient cover in the depot during that period..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setDeclineModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDecline}>Confirm Decline</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageRequests;
